import { useState, useMemo } from "react";
import { AlertTriangle, DollarSign, RefreshCw, Download, Clock, CheckCircle2, SlidersHorizontal } from "lucide-react";
import type { AdminPayment } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { payments as paymentsApi } from "../../api/endpoints";
import { downloadCsv } from "../../exportCsv";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { FilterDrawer, FilterFieldDef } from "../../components/ui/FilterDrawer";
import { KpiCard } from "../../components/ui/KpiCard";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchBar } from "../../components/ui/SearchBar";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Payments ──────────────────────────────────────────────────────────────────

export function PaymentsView({ addToast }: { addToast: AddToast }) {
  const { ADMIN_PAYMENTS, refetchPayments, refetchTalents, refetchOrgs } = useAdminData();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orgFilter, setOrgFilter] = useState("All");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // These move real money: the admin backend hands them to the app API, which
  // owns the escrow rules, writes the ledger rows and recomputes both wallets.
  function settle(p: AdminPayment, status: "Released" | "Refunded") {
    const verb = status === "Released" ? "release" : "refund";
    const confirmed = window.confirm(
      `${status === "Released" ? "Release" : "Refund"} ${p.amount} for "${p.postingTitle}"?

` +
        `${status === "Released" ? `${p.talentName} receives the net amount.` : `${p.orgName} gets the escrow back.`}`
    );
    if (!confirmed) return;

    const reason = status === "Refunded" ? window.prompt("Reason for the refund (shown to both parties):", "Refunded by Donatella support") ?? undefined : undefined;

    setBusyId(p.id);
    paymentsApi.update(p.id, { status, reason })
      .then(() => {
        refetchPayments();
        refetchTalents();
        refetchOrgs();
        addToast(`Payment ${status.toLowerCase()}`, status === "Released" ? "success" : "info");
      })
      .catch((e) => addToast(e instanceof Error ? e.message : `Failed to ${verb} payment`, "error"))
      .finally(() => setBusyId(null));
  }

  function exportPayments(rows: AdminPayment[]) {
    const count = downloadCsv("donatella-payments", rows, [
      { header: "Transaction", value: (p) => p.txnRef },
      { header: "Talent", value: (p) => p.talentName },
      { header: "Organisation", value: (p) => p.orgName },
      { header: "Engagement", value: (p) => p.postingTitle },
      { header: "Amount", value: (p) => p.amount },
      { header: "Platform fee", value: (p) => p.platformFee },
      { header: "Net to talent", value: (p) => p.netToTalent },
      { header: "Status", value: (p) => p.status },
      { header: "Date", value: (p) => p.date },
    ]);
    addToast(`Exported ${count} payments`, "success");
  }

  const uniqueOrgs = useMemo(() => ["All", ...Array.from(new Set(ADMIN_PAYMENTS.map((p) => p.orgName))).sort()], [ADMIN_PAYMENTS]);

  const payFilterFields: FilterFieldDef[] = [
    { type: "chips", key: "status", label: "Status", options: ["All", "Released", "Escrow", "Pending", "Failed", "Refunded"] },
    { type: "date", key: "dateFrom", label: "Date from" },
    { type: "date", key: "dateTo", label: "Date to" },
    { type: "select", key: "org", label: "Organisation", options: uniqueOrgs },
  ];
  const filterValues = { status: statusFilter, dateFrom, dateTo, org: orgFilter };
  const hasFilter = statusFilter !== "All" || dateFrom !== "" || dateTo !== "" || orgFilter !== "All";

  const filtered = useMemo(() => ADMIN_PAYMENTS.filter((p) => {
    const s = search.toLowerCase();
    const match = !s || p.talentName.toLowerCase().includes(s) || p.orgName.toLowerCase().includes(s) || p.txnRef.toLowerCase().includes(s);
    const ms = statusFilter === "All" || p.status === statusFilter;
    const mo = orgFilter === "All" || p.orgName === orgFilter;
    return match && ms && mo;
  }), [ADMIN_PAYMENTS, search, statusFilter, orgFilter]);

  const totalVolume = filtered.reduce((acc, p) => acc + parseInt(p.amount.replace(/[^0-9]/g, ""), 10), 0);

  return (
    <div>
      <PageHeader title="Payments" sub="All transactions on the platform" actions={
        <Btn variant="ghost" onClick={() => exportPayments(filtered)} iconOnlyPhone><Download size={13} /> Export CSV</Btn>
      } />

      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Payments" fields={payFilterFields} values={filterValues}
        onApply={(v) => { setStatusFilter(v.status ?? "All"); setDateFrom(v.dateFrom ?? ""); setDateTo(v.dateTo ?? ""); setOrgFilter(v.org ?? "All"); }} />

      <div className="rg-4" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "12px", marginBottom: "22px" }}>
        <KpiCard icon={<DollarSign size={16} />} label="Shown total" value={`SAR ${totalVolume.toLocaleString()}`} />
        <KpiCard icon={<CheckCircle2 size={16} />} label="Released" value={filtered.filter((p) => p.status === "Released").length.toString()} />
        <KpiCard icon={<Clock size={16} />} label="In escrow" value={filtered.filter((p) => p.status === "Escrow").length.toString()} />
        <KpiCard icon={<AlertTriangle size={16} />} label="Failed / Pending" value={filtered.filter((p) => p.status === "Failed" || p.status === "Pending").length.toString()} />
      </div>

      <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search payments…" />
        <button className="filter-btn" onClick={() => setDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${hasFilter ? CORAL : T.border}`, background: hasFilter ? `rgba(209,0,75,0.06)` : T.s2, color: hasFilter ? CORAL : T.sub, transition: "all 0.15s" }}>
          <SlidersHorizontal size={13} /> Filters
        </button>
        {hasFilter && <button onClick={() => { setStatusFilter("All"); setDateFrom(""); setDateTo(""); setOrgFilter("All"); }} style={{ fontSize: "11px", color: CORAL, fontFamily: F, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear</button>}
      </div>

      <Card>
        <TableWrap><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {["Transaction", "Talent", "Organisation", "Amount", "Platform fee", "Net", "Status", "Date", "Actions"].map((h) => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                <td style={{ padding: "12px 14px" }}>
                  <p style={{ fontSize: "10px", fontFamily: "'Space Mono', monospace", color: T.sub }}>{p.txnRef}</p>
                </td>
                <td style={{ padding: "12px 14px", fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{p.talentName}</td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.sub, fontFamily: F }}>{p.orgName}</td>
                <td style={{ padding: "12px 14px", fontSize: "12px", fontWeight: 700, color: T.text, fontFamily: F }}>{p.amount}</td>
                <td style={{ padding: "12px 14px", fontSize: "11px", color: T.sub, fontFamily: F }}>{p.platformFee}</td>
                <td style={{ padding: "12px 14px", fontSize: "12px", fontWeight: 600, color: "#166534", fontFamily: F }}>{p.netToTalent}</td>
                <td style={{ padding: "12px 14px" }}><StatusBadge status={p.status} /></td>
                <td style={{ padding: "12px 14px", fontSize: "11px", color: T.muted, fontFamily: F }}>{p.date}</td>
                <td style={{ padding: "12px 14px" }}>
                  {p.status === "Escrow" && (
                    <Btn variant="primary" size="xs" disabled={busyId === p.id} onClick={() => settle(p, "Released")}>
                      {busyId === p.id ? <RefreshCw size={11} /> : null} Release
                    </Btn>
                  )}
                  {/* Only escrow that is still held can come back — a released
                      milestone has already paid out. */}
                  {p.status === "Escrow" && (
                    <Btn variant="ghost" size="xs" disabled={busyId === p.id} onClick={() => settle(p, "Refunded")}>Refund</Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></TableWrap>
      </Card>
    </div>
  );
}
